import { useEffect, useState } from 'react';
import { listServices, createService, updateService, deleteService } from '../adminApi';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Save, Layers } from 'lucide-react';

const EMPTY_FORM = {
  tag: '',
  title: '',
  description: '',
  image: '',
  reverse_layout: false,
  is_active: true,
  display_order: 999,
};

function toForm(svc) {
  return {
    tag: svc.tag ?? '',
    title: svc.title ?? '',
    description: svc.description ?? '',
    image: svc.image ?? '',
    reverse_layout: svc.reverse_layout ?? false,
    is_active: svc.is_active ?? true,
    display_order: svc.display_order ?? 999,
  };
}

// ── Service modal ─────────────────────────────────────────────────────────────
function ServiceModal({ service, onClose, onSaved }) {
  const isEdit = Boolean(service);
  const [form, setForm] = useState(isEdit ? toForm(service) : EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, display_order: parseInt(form.display_order, 10) || 999 };
      let result;
      if (isEdit) {
        result = await updateService(service.id, payload);
        toast.success('Hizmet güncellendi.');
      } else {
        result = await createService(payload);
        toast.success('Hizmet oluşturuldu.');
      }
      onSaved(result, isEdit);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#111] border border-white/[0.09] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]">
          <h2 className="text-white font-semibold text-sm">
            {isEdit ? 'Hizmeti Düzenle' : 'Yeni Hizmet'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg text-white/35 hover:text-white/70 transition-colors">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <FormField label="Etiket *">
            <input name="tag" value={form.tag} onChange={handleChange} required placeholder="KONUT PROJELERİ" className={INPUT} />
          </FormField>
          <FormField label="Başlık *">
            <textarea name="title" value={form.title} onChange={handleChange} required rows={2} placeholder={"Yaşam Alanları\nTasarlıyoruz"} className={INPUT + ' resize-none'} />
          </FormField>
          <FormField label="Açıklama *">
            <textarea name="description" value={form.description} onChange={handleChange} required rows={4} className={INPUT + ' resize-none'} />
          </FormField>
          <FormField label="Görsel URL">
            <input name="image" value={form.image} onChange={handleChange} placeholder="/images/4-hizmetler/..." className={INPUT} />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Görüntülenme Sırası">
              <input type="number" name="display_order" value={form.display_order} onChange={handleChange} min={0} className={INPUT} />
            </FormField>
            <FormField label="Seçenekler">
              <div className="flex flex-col gap-2 pt-1.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="reverse_layout" checked={form.reverse_layout} onChange={handleChange} className="accent-[#c5a059] w-3.5 h-3.5" />
                  <span className="text-sm text-white/65">Ters Yerleşim</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} className="accent-[#c5a059] w-3.5 h-3.5" />
                  <span className="text-sm text-white/65">Aktif</span>
                </label>
              </div>
            </FormField>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-white/40 hover:text-white/70 transition-colors">
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-[#c5a059] hover:bg-[#d4af6a] disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold px-5 py-2 rounded-lg text-sm transition-colors"
            >
              <Save size={13} />
              {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // null | 'new' | service object
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    listServices()
      .then(setServices)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSaved = (result, isEdit) => {
    if (isEdit) {
      setServices((ss) => ss.map((s) => (s.id === result.id ? { ...s, ...result } : s)));
    } else {
      setServices((ss) => [...ss, result]);
    }
    setModal(null);
  };

  const handleDelete = async (id) => {
    try {
      await deleteService(id);
      setServices((ss) => ss.filter((s) => s.id !== id));
      setDeleteId(null);
      toast.success('Hizmet silindi.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <PageLoader />;
  if (error) return <PageError message={error} />;

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Hizmetler</h1>
          <p className="text-white/35 text-sm mt-0.5">{services.length} hizmet</p>
        </div>
        <button
          onClick={() => setModal('new')}
          className="flex items-center gap-2 bg-[#c5a059] hover:bg-[#d4af6a] text-black font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
        >
          <Plus size={15} />
          Yeni Hizmet
        </button>
      </div>

      {services.length === 0 ? (
        <EmptyState icon={Layers} text="Henüz hizmet yok." />
      ) : (
        <div className="bg-[#111] border border-white/[0.07] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
          <div className="min-w-[640px]">
          {/* Head */}
          <div className="grid grid-cols-[80px_2fr_3fr_80px_80px_100px] gap-3 px-4 py-2.5 border-b border-white/[0.06] text-[10px] text-white/30 uppercase tracking-wider">
            <span>Sıra</span>
            <span>Etiket / Başlık</span>
            <span>Açıklama</span>
            <span className="text-center">Ters</span>
            <span className="text-center">Aktif</span>
            <span className="text-right">İşlemler</span>
          </div>

          {services
            .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999))
            .map((svc) => (
              <div
                key={svc.id}
                className="grid grid-cols-[80px_2fr_3fr_80px_80px_100px] gap-3 px-4 py-3.5 border-b border-white/[0.04] last:border-0 items-center hover:bg-white/[0.015] transition-colors"
              >
                <span className="text-sm text-white/45">{svc.display_order}</span>

                <div className="min-w-0">
                  <p className="text-[11px] text-[#c5a059] font-medium uppercase tracking-wider truncate">
                    {svc.tag}
                  </p>
                  <p className="text-sm text-white/75 truncate whitespace-pre-line leading-tight mt-0.5">
                    {svc.title}
                  </p>
                </div>

                <p className="text-sm text-white/40 truncate">{svc.description}</p>

                <div className="flex justify-center">
                  <ActiveDot active={svc.reverse_layout} />
                </div>

                <div className="flex justify-center">
                  <ActiveDot active={svc.is_active} />
                </div>

                <div className="flex items-center justify-end gap-1">
                  {deleteId === svc.id ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDelete(svc.id)}
                        className="text-[11px] bg-red-500/15 text-red-400 border border-red-500/20 px-2 py-1 rounded-lg hover:bg-red-500/25 transition-colors"
                      >
                        Sil
                      </button>
                      <button
                        onClick={() => setDeleteId(null)}
                        className="text-[11px] text-white/35 hover:text-white/60 px-1.5 py-1"
                      >
                        İptal
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => setModal(svc)}
                        className="p-1.5 rounded-lg text-white/35 hover:text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors"
                        title="Düzenle"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteId(svc.id)}
                        className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Sil"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <ServiceModal
          service={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function ActiveDot({ active }) {
  return (
    <span
      className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-400' : 'bg-white/15'}`}
    />
  );
}

const INPUT =
  'w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 focus:bg-white/[0.06] transition-all';

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
        {label}
      </label>
      {children}
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
    </div>
  );
}

function PageError({ message }) {
  return (
    <div className="p-8">
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 text-red-400 text-sm">
        Hata: {message}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-white/20">
      <Icon size={32} className="mb-3" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
